import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Clock, Shield, FileText, Users, ArrowRight } from "lucide-react";
import SetmoreBooking from "@/components/SetmoreBooking";
import SEO from "@/components/SEO";
const DrugTesting = () => {
  const dotFeatures = ["5-Panel Drug Test", "10-Panel Drug Test", "Federal Compliance", "DOT Certified Collectors", "Chain of Custody Documentation", "24-48 Hour Results", "MRO Review Available"];
  const nonDotFeatures = ["5-Panel Drug Test", "10-Panel Drug Test", "12-Panel Drug Test", "Expanded Opiates Panel", "Same Day Results Available", "Flexible Testing Options", "Employer Customizable Panels"];
  return <>
    <SEO 
      title="DOT & Non-DOT Drug Testing | NearLab Services"
      description="Professional DOT and Non-DOT drug testing services. Federal compliance testing for transportation industry and flexible testing for employers. Fast, accurate results."
      keywords="DOT drug testing, non-DOT drug testing, workplace drug testing, CDL drug test, employer drug screening, drug testing near me"
      url="https://nearlabservices.com/drug-testing"
    />
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            DOT & Non-DOT
            <span className="text-blue-600"> Drug Testing</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Comprehensive drug testing services for employers, individuals, and DOT-regulated industries. 
            Fast, accurate, and compliant testing with certified professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3" 
              onClick={() => window.location.href = '/booking'}
              aria-label="Schedule DOT drug testing appointment"
            >
              Schedule DOT Test
              <ArrowRight className="ml-2" size={18} />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3" 
              onClick={() => window.location.href = '/booking'}
              aria-label="Schedule non-DOT drug testing appointment"
            >
              Schedule Non-DOT Test
            </Button>
          </div>
        </div>
      </section>

      {/* DOT vs Non-DOT Comparison */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Choose Your Testing Type
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Understanding the difference between DOT and Non-DOT testing helps ensure compliance 
              and meets your specific requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* DOT Testing Card */}
            <Card className="border-2 border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
                <div className="flex items-center gap-2">
                  <Shield className="h-6 w-6" />
                  <CardTitle className="text-2xl">DOT Testing</CardTitle>
                </div>
                <CardDescription className="text-blue-100">
                  Federal compliance for transportation industry
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Required For:</h4>
                  <ul className="text-gray-600 space-y-1">
                    <li>• Commercial Drivers (CDL)</li>
                    <li>• Aviation Personnel</li>
                    <li>• Railroad Workers</li>
                    <li>• Pipeline Workers</li>
                    <li>• Maritime Workers</li>
                  </ul>
                </div>
                
                <div className="mb-6">
                  
                  
                </div>

                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <span className="font-semibold text-blue-900">Starting at $70</span>
                  </div>
                  <p className="text-blue-700 text-sm">Includes federal compliance documentation</p>
                </div>

                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700" 
                  onClick={() => window.location.href = '/booking'}
                  aria-label="Schedule DOT compliant drug testing"
                >
                  Schedule DOT Test
                </Button>
              </CardContent>
            </Card>

            {/* Non-DOT Testing Card */}
            <Card className="border-2 border-green-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-t-lg">
                <div className="flex items-center gap-2">
                  <Users className="h-6 w-6" />
                  <CardTitle className="text-2xl">Non-DOT Testing</CardTitle>
                </div>
                <CardDescription className="text-green-100">
                  Flexible testing for employers and individuals
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Ideal For:</h4>
                  <ul className="text-gray-600 space-y-1">
                    <li>• Pre-Employment Screening</li>
                    <li>• Random Workplace Testing</li>
                    <li>• Post-Accident Testing</li>
                    <li>• Personal Testing</li>
                    <li>• Court-Ordered Testing</li>
                  </ul>
                </div>

                <div className="mb-6">
                  
                  
                </div>

                <div className="bg-green-50 p-4 rounded-lg mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-green-600" />
                    <span className="font-semibold text-green-900">Starting at $60</span>
                  </div>
                  <p className="text-green-700 text-sm">Quick results and flexible scheduling</p>
                </div>

                <Button 
                  className="w-full bg-green-600 hover:bg-green-700" 
                  onClick={() => window.location.href = '/booking'}
                  aria-label="Schedule non-DOT drug testing"
                >
                  Schedule Non-DOT Test
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testing Process */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Simple Testing Process
            </h2>
            <p className="text-gray-600">
              Our streamlined process ensures accurate results with minimal disruption to your schedule.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Schedule Online</h3>
              <p className="text-gray-600">Book your appointment quickly through our online system</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Visit Our Lab</h3>
              <p className="text-gray-600">Quick sample collection by certified professionals</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Get Results</h3>
              <p className="text-gray-600">Receive secure, confidential results within 24-48 hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-green-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Schedule Your Drug Test?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Professional, confidential, and compliant testing services available today.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3" 
            onClick={() => window.location.href = '/booking'}
            aria-label="Schedule your drug test appointment now"
          >
            Schedule Your Test Now
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </section>
      
      {/* Setmore Booking Integration */}
      <SetmoreBooking />
    </div>
  </>;
};
export default DrugTesting;